import { useMemo } from 'react';
import { Card, CardContent, Typography, Box, Grid, Chip, Divider, Paper } from '@mui/material';
import {
  Timeline as ProgressIcon,
  VerifiedUser as QualityIcon,
  PlayArrow as InProgressIcon,
  CheckCircle as CompletedIcon,
  PauseCircleOutline as NotStartedIcon,
  Cancel as CancelledIcon,
  TrendingUp as TrendingIcon,
  CalendarToday as DateIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Line,
  LineChart,
} from 'recharts';
import { useProjectProgressTrackingHistory } from '../../../../hooks/useProjectProgressTrackingHistory';

const clampPercentage = (value) => Math.min(100, Math.max(0, Number(value) || 0));

const formatChartDate = (value) => {
  if (!value || value === 'Actual') return value;

  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const parsed = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleDateString('es-AR');
};

/**
 * Vista detallada de análisis de proyecto
 * Versión expandida con múltiples gráficos y métricas detalladas
 */
const ProjectAnalyticsDetailed = ({ project }) => {
  // Configuración de colores para estados
  const statusConfig = {
    not_started: {
      color: 'default',
      icon: NotStartedIcon,
      label: 'Sin Empezar',
      bgcolor: '#f5f5f5',
    },
    in_progress: {
      color: 'warning',
      icon: InProgressIcon,
      label: 'En Progreso',
      bgcolor: '#fff3e0',
    },
    completed: { color: 'success', icon: CompletedIcon, label: 'Completado', bgcolor: '#e8f5e8' },
    cancelled: { color: 'error', icon: CancelledIcon, label: 'Cancelado', bgcolor: '#ffebee' },
  };

  const status = statusConfig[project.status] || statusConfig.not_started;
  const StatusIcon = status.icon;
  const { data: progressHistory, isLoading: progressHistoryLoading } =
    useProjectProgressTrackingHistory(project?.uuid, {
      enabled: !!project?.uuid,
    });

  // Datos para gráfico circular de progreso
  const completedProgress = project.progressMetrics.completionPercentage || 0;
  const pendingProgress = Math.max(0, 100 - completedProgress);
  const progressPieData = [
    {
      name: 'Completado',
      value: completedProgress,
      fill: '#4caf50',
    },
    {
      name: 'Pendiente',
      value: pendingProgress,
      fill: '#e0e0e0',
    },
  ].filter((item) => item.value > 0);

  // Datos para gráfico circular de calidad
  const qualityPieData = [
    {
      name: 'Completados',
      value: project.qualityMetrics.completedControls,
      fill: '#4caf50',
    },
    {
      name: 'En Progreso',
      value: project.qualityMetrics.inProgressControls,
      fill: '#ff9800',
    },
    {
      name: 'Fallidos',
      value: project.qualityMetrics.failedControls,
      fill: '#f44336',
    },
    {
      name: 'Sin Empezar',
      value:
        project.qualityMetrics.totalControls -
        project.qualityMetrics.completedControls -
        project.qualityMetrics.inProgressControls -
        project.qualityMetrics.failedControls,
      fill: '#9e9e9e',
    },
  ].filter((item) => item.value > 0);

  // Datos para distribución de severidad
  const severityData = [
    {
      name: 'Baja',
      value: project.qualityMetrics.qualityDistribution.low,
      fill: '#4caf50',
    },
    {
      name: 'Media',
      value: project.qualityMetrics.qualityDistribution.medium,
      fill: '#ff9800',
    },
    {
      name: 'Alta',
      value: project.qualityMetrics.qualityDistribution.high,
      fill: '#f44336',
    },
  ];

  const timelineData = useMemo(() => {
    const historyPoints = Array.isArray(progressHistory?.timeline)
      ? progressHistory.timeline
      : [];

    const normalizedHistory = historyPoints
      .map((point) => ({
        periodo: point?.date,
        progreso: clampPercentage(point?.project_total_current_percentage),
        updatesCount: Number(point?.updates_count) || 0,
      }))
      .filter((point) => point.periodo)
      .sort((a, b) => a.periodo.localeCompare(b.periodo));

    if (normalizedHistory.length > 0) {
      return normalizedHistory;
    }

    return [
      {
        periodo: 'Actual',
        progreso: clampPercentage(project.progressMetrics.completionPercentage),
        updatesCount: 0,
      },
    ];
  }, [progressHistory, project.progressMetrics.completionPercentage]);

  const qualityWeightLabel =
    project.qualityMetrics.totalWeight > 0
      ? `${project.qualityMetrics.completedWeight.toFixed(1)}/${project.qualityMetrics.totalWeight.toFixed(1)} incidencia`
      : `${project.qualityMetrics.completedControls}/${project.qualityMetrics.totalControls} controles`;

  return (
    <Card
      elevation={3}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header del proyecto */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
          gap={2}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              {project.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {project.project_number} • {project.description || 'Sin descripción'}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <DateIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Inicio: {project.start_date || 'Sin fecha'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {project.subscribers?.length || 0} asignados
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
            gap={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Chip label={status.label} color={status.color} icon={<StatusIcon />} />
            <Box textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Progreso
              </Typography>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                {(() => {
                  const progreso = project.progressMetrics.completionPercentage || 0;
                  const calidad = project.qualityMetrics.successRate || 0;
                  return ((progreso + calidad) / 2).toFixed(1);
                })()}
                %
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Métricas principales */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <ProgressIcon sx={{ fontSize: '2rem', color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                {project.progressMetrics.completionPercentage.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Progreso
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {project.progressMetrics.tasksCompleted}/{project.progressMetrics.totalTasks} tareas
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <QualityIcon sx={{ fontSize: '2rem', color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                {project.qualityMetrics.successRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avance Calidad
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {qualityWeightLabel}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <TrendingIcon sx={{ fontSize: '2rem', color: 'info.main', mb: 1 }} />
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                {project.qualityMetrics.controlSuccessRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Controles OK
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {project.qualityMetrics.completedControls}/{project.qualityMetrics.totalControls}{' '}
                controles
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
              <StatusIcon sx={{ fontSize: '2rem', color: `${status.color}.main`, mb: 1 }} />
              <Typography variant="h4" color={`${status.color}.main`} sx={{ fontWeight: 'bold' }}>
                {project.daysActive}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Días Activo
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tiempo transcurrido
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Gráficos de análisis */}
        <Grid container spacing={3}>
          {/* Gráfico de progreso circular */}
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, height: '300px' }}>
              <Typography variant="h6" gutterBottom>
                Distribución de Progreso
              </Typography>
              <ResponsiveContainer width="100%" height="80%">
                <PieChart>
                  <Pie
                    data={progressPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {progressPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Porcentaje']} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Gráfico de calidad circular */}
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, height: '300px' }}>
              <Typography variant="h6" gutterBottom>
                Estados de Calidad
              </Typography>
              <ResponsiveContainer width="100%" height="80%">
                <PieChart>
                  <Pie
                    data={qualityPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {qualityPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => [`${value} controles`, 'Cantidad']} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Gráfico de severidad */}
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, height: '300px' }}>
              <Typography variant="h6" gutterBottom>
                Distribución por Severidad
              </Typography>
              <ResponsiveContainer width="100%" height="80%">
                <BarChart data={severityData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Progreso en el tiempo */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, height: '300px' }}>
              <Typography variant="h6" gutterBottom>
                Evolución del Proyecto
              </Typography>
              {progressHistoryLoading && (
                <Typography variant="caption" color="text.secondary">
                  Cargando historial...
                </Typography>
              )}
              <ResponsiveContainer width="100%" height="80%">
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="periodo" tickFormatter={formatChartDate} />
                  <YAxis domain={[0, 100]} />
                  <RechartsTooltip
                    labelFormatter={formatChartDate}
                    formatter={(value, name, item) => {
                      if (name === 'progreso') {
                        const updatesCount = item?.payload?.updatesCount;
                        const suffix = updatesCount > 0 ? ` (${updatesCount} act.)` : '';
                        return [`${Number(value).toFixed(1)}%${suffix}`, 'Avance total'];
                      }

                      return [value, name];
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="progreso"
                    stroke="#2196f3"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ProjectAnalyticsDetailed;
